import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { 
  getSkills, 
  updateSkills, 
  deleteSkill, 
  deleteAllSkills 
} from '../../../services/candidate/skillsService';
import { searchSkills } from '../../../services/candidate/skillSuggestionService';
import ConfirmModal from '../../../components/ui/ConfirmModal';

const Skills = ({ profileData, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // New state for bulk add modal
  const [bulkSkillsInput, setBulkSkillsInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [inputError, setInputError] = useState('');
  
  const suggestionRef = useRef(null);
  const inputRef = useRef(null);

  // States for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionData, setActionData] = useState(null);

  // Fetch skills on component mount
  useEffect(() => {
    fetchSkillsData();
  }, []);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSkillsData = async () => {
    try {
      setLoading(true);
      const response = await getSkills();
      
      if (response.success) {
        setSkills(response.data?.skills || []);
      } else {
        toast.error('Failed to load skills');
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
      toast.error('Something went wrong on loading skills');
    } finally {
      setLoading(false);
    }
  };

  // Handle input change for suggestions
  const handleInputChange = (e) => {
    const value = e.target.value;
    setBulkSkillsInput(value);
    setInputError('');
    
    if (value.trim().length >= 1) {
      const results = searchSkills(value);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (skill) => {
    // Check if skill already exists in the selected list
    if (selectedSkills.includes(skill)) {
      toast.error(`${skill} is already added`);
      return;
    }
    
    setSelectedSkills([...selectedSkills, skill]);
    setBulkSkillsInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    setInputError('');
  };

  // Handle adding custom skill (not from suggestions)
  const handleAddCustomSkill = () => {
    const skillToAdd = bulkSkillsInput.trim();
    
    if (!skillToAdd) {
      setInputError('Please enter a skill');
      return;
    }

    // Validation
    if (skillToAdd.length < 2) {
      setInputError('Skill must be at least 2 characters');
      return;
    }

    if (skillToAdd.length > 50) {
      setInputError('Skill must be less than 50 characters');
      return;
    }

    if (!/^[a-zA-Z0-9\s\-\+#\.]+$/.test(skillToAdd)) {
      setInputError('Skill contains invalid characters');
      return;
    }

    // Check if skill already exists in selected list
    if (selectedSkills.includes(skillToAdd)) {
      setInputError('Skill already added');
      return;
    }

    setSelectedSkills([...selectedSkills, skillToAdd]);
    setBulkSkillsInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    setInputError('');
  };

  // Handle removing skill from selected list
  const handleRemoveSelectedSkill = (skillToRemove) => {
    setSelectedSkills(selectedSkills.filter(skill => skill !== skillToRemove));
  };

  // Handle submit bulk skills
  const handleSubmitBulkSkills = async () => {
    if (selectedSkills.length === 0) {
      toast.error('Please add at least one skill');
      return;
    }

    setSubmitting(true);

    try {
      // Combine existing skills with new selected skills
      const allSkills = [...new Set([...skills, ...selectedSkills])];
      
      const payload = {
        skills: allSkills
      };

      const response = await updateSkills(payload);

      if (response.success) {
        toast.success(`${selectedSkills.length} skill(s) added successfully!`);
        setSkills(allSkills);
        setSelectedSkills([]);
        setIsModalOpen(false);
        
        // Trigger parent update for profile completion
        if (onUpdate && response.completionScore !== undefined) {
          onUpdate({ completionScore: response.completionScore });
        }
      } else {
        toast.error(response.message || 'Failed to add skills');
      }
    } catch (error) {
      console.error('Error adding skills:', error);
      toast.error('Error adding skills. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSkill = async (skillName) => {
    if (!skillName) return;
    
    try {
      const response = await deleteSkill(skillName);
      
      if (response.success) {
        //toast.success(response.message || 'Skill deleted successfully!');
        const updatedSkills = skills.filter(skill => skill !== skillName);
        setSkills(updatedSkills);

        if (onUpdate && updatedSkills.length === 0 && response.completionScore !== undefined) {
          onUpdate({ completionScore: response.completionScore });
        }
      } else {
        toast.error(response.message || 'Failed to delete skill');
      }
    } catch (error) {
      console.error('Error deleting skill:', error);
      toast.error('Something went wrong on deleting skill. Please try again.');
    }
  };

  const handleDeleteAllSkills = async () => {
    setConfirmAction('deleteAll');
    setActionData(null);
    setShowConfirmModal(true);    
  };

  const handleConfirmAction = async () => {
    if (confirmAction === 'deleteAll') {
      await executeDeleteAllSkills();
    }
  };

  const executeDeleteAllSkills = async () => {
    setDeletingAll(true);
    
    try {
      const response = await deleteAllSkills();
      
      if (response.success) {
        toast.success(response.message || 'All skills deleted successfully!');
        setSkills([]);
        
        if (onUpdate && response.completionScore !== undefined) {
          onUpdate({ completionScore: response.completionScore });
        }
      } else {
        toast.error(response.message || 'Failed to delete all skills');
      }
    } catch (error) {
      console.error('Error deleting all skills:', error);
      toast.error('Something went wrong on deleting all skills. Please try again.');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomSkill();
    }
  };

  const openModal = () => {
    setSelectedSkills([]);
    setBulkSkillsInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    setInputError('');
    setIsModalOpen(true);
  };

  const getModalConfig = () => {
    switch (confirmAction) {
      case 'deleteAll':
        return {
          title: 'Delete All Skills',
          message: 'Are you sure you want to delete all skills? This action cannot be undone.',
          confirmText: 'Delete All',
          confirmColor: 'btn-primary'
        };
      default:
        return {
          title: 'Confirm Action',
          message: 'Are you sure you want to proceed?',
          confirmText: 'Confirm',
          confirmColor: 'bg-primary-600 hover:bg-primary-700'
        };
    }
  };

  const modalConfig = getModalConfig();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="skills">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Add relevant skills to improve your job matches</h3>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 btn-primary text-white px-4 py-2 rounded-lg transition-colors"
        >
          <i className="fas fa-plus"></i>
          Add Skills
        </button>
      </div>

      {/* Skills Display */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-gray-700">Your Skills ({skills.length})</h4>
          {skills.length > 0 && (
            <button
              onClick={handleDeleteAllSkills}
              disabled={deletingAll}
              className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deletingAll ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-1"></i>
                  Deleting...
                </>
              ) : (
                'Delete All Skills'
              )}
            </button>
          )}
        </div>

        {skills.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <i className="fas fa-code text-4xl text-gray-400 mb-3"></i>
            <p className="text-gray-600">No skills added yet</p>
            <p className="text-gray-500 text-sm mt-1">
              Click the "Add Skills" button to showcase your expertise to employers
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {skills.map((skill, index) => (
              <div
                key={`${skill}-${index}`}
                className="bg-white border border-gray-200 rounded-full px-4 py-2 flex items-center gap-2 hover:border-gray-300 transition-colors"
              >
                <span className="text-gray-800">{skill}</span>
                <button
                  onClick={() => handleDeleteSkill(skill)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove skill"
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal for Adding Skills */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold">Add Skills</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Search and select skills from suggestions or add your own
                  </p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              {/* Skill Input with Suggestions */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search or Add Skills
                </label>
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        ref={inputRef}
                        type="text"
                        value={bulkSkillsInput}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        onFocus={() => bulkSkillsInput.trim().length >= 1 && setShowSuggestions(true)}
                        placeholder="Type to search skills or enter custom skill"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      
                      {/* Suggestions Dropdown */}
                      {showSuggestions && suggestions.length > 0 && (
                        <div
                          ref={suggestionRef}
                          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                        >
                          {suggestions.map((skill, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handleSuggestionClick(skill)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                            >
                              <span className="text-gray-800">{skill}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddCustomSkill}
                      className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {inputError && (
                    <p className="mt-1 text-sm text-red-500">{inputError}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Type and press Enter or click Add to add custom skills not in suggestions
                  </p>
                </div>
              </div>

              {/* Selected Skills */}
              {selectedSkills.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Skills to Add ({selectedSkills.length})
                  </label>
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                    {selectedSkills.map((skill, index) => (
                      <div
                        key={index}
                        className="bg-white border border-gray-200 rounded-full px-3 py-1.5 flex items-center gap-2"
                      >
                        <span className="text-gray-800 text-sm">{skill}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedSkill(skill)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <i className="fas fa-times text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Tips */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <i className="fas fa-lightbulb"></i>
                  Tips for adding skills:
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Search from pre-loaded skills suggestions</li>
                  <li>• Add custom skills if not found in suggestions</li>
                  <li>• Add multiple skills at once before submitting</li>
                  <li>• Skills should be at least 2 characters long</li>
                  <li>• Maximum 50 characters per skill</li>
                  <li>• Duplicate skills are automatically prevented</li>
                </ul>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitBulkSkills}
                  disabled={submitting || selectedSkills.length === 0}
                  className="px-6 py-2 btn-primary text-white rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Adding...
                    </>
                  ) : (
                    `Add ${selectedSkills.length} Skill${selectedSkills.length !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAction}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        confirmColor={modalConfig.confirmColor}
      />
    </div>
  );
};

export default Skills;